import { useState, useEffect, useRef, MutableRefObject } from "react";
import { Popover, PopoverTrigger } from "./components/popover";
import { Info, CheckCircle2, AlertCircle, Pencil, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { fetchSalaryInsuranceData } from "../../../utils/api";
import { SalaryinsuranceCalculatorPricing } from "../../../types/SalaryinsuranceCalculatorPricing";
import {MonthButton, Wrapper, CalculatorContainer, ConfirmationOuter, ConfirmationBox, ConfirmationIconWrap, ConfirmationContent, ConfirmationTitle, ConfirmationText, SalaryCard, CollapsedButton, CollapsedLeft, CollapsedTextGroup, CollapsedLabel, CollapsedValue, EditBadge, ExpandedContent, FieldGroup, FieldLabel, BlockLabel, SmallLabel, InfoTriggerButton, InfoTriggerButtonLight, InfoPopoverContent, InfoPopoverContentWide, PopoverText, InputWrap, CurrencyPrefix, SalaryInput, ErrorBanner, ResultsSection, MainGrid, ControlsCard, PeriodGroup, MonthButtonRow, SliderGroup, SliderHeaderRow, SliderValueWrap, SliderValue, SliderUnit, StyledSlider, SliderMetaRow, BadgeTitle, BadgeText, BadgeLink, RecommendedBadge, InfoBadge, HighlightText, ChooseLink, RecommendCTA, ResultsCard, ResultsHeader, ResultsHeaderRow, ResultsHeaderLabel, ResultsAmountWrap, ResultsAmount, ResultsAmountUnit, ExceedsLimitWrap, ExceedsTooltip, TooltipTitle, ChartArea, BreakdownWrap, BarLabelsRow, BarTrack, BarAllowance, BarInsurance, BarMarker, LegendRow, LegendItem, LegendDot, LegendText, CtaCard, CtaTop, CtaHeadingRow, CtaSubtitle, CtaRow, PriceWrap, PriceValue, PriceUnit, NextButton, Disclaimer, BelowThresholdCard, BelowThresholdInner, BelowHeading, BelowSubtitle, BelowInfoLine, BelowInfoLineSpaced, BelowBenefitsWrap, BelowBenefitsTitle, BelowBenefitsList, BelowBenefitItem, BelowBullet, BelowBenefitText, PopupOverlay, PopupContent, PopupClose } from "./Calculator.style";
import Analytics from "../../../utils/analytics";

//Pricindata mapping
export const mapPricingData = (data: SalaryinsuranceCalculatorPricing[]): Record<number, Record<number, number>> => {
  const result: Record<number, Record<number, number>> = {
    3: {},
    6: {},
    9: {}
  };

  data.forEach(item => {
    result[3][item.maksimal] = item.three_mnd;
    result[6][item.maksimal] = item.six_mnd;
    result[9][item.maksimal] = item.nine_mnd;
  });
  
  return result;
};

// // Helper function to calculate price using the formula: ((X+22041)/0.9)/(1-0.08)
// export const calculatePriceFormula = (salary: number, coverageAmount: number): number => {
//   // Formula: ((coverage + DAILY_ALLOWANCE) / 0.9) / (1 - 0.08)
//   // This helps verify pricing for a given salary and coverage
//   const DAILY_ALLOWANCE = 22041;
//   const result = ((coverageAmount + DAILY_ALLOWANCE) / 0.9) / (1 - 0.08);
//   return Math.round(result);
// };

// // Test calculation for 42000 salary
// export const testCalculation = (salary: number = 42000) => {
//   const adjustedMaxPayout = Math.round(salary * 0.9 * (1 - 0.08));
//   const maxAllowedInsurance = Math.max(0, adjustedMaxPayout - 22041);
  
//   // Calculate for different coverage amounts (2000 to 30000 in steps of 1000)
//   const results: Record<number, number> = {};
//   for (let coverage = 2000; coverage <= 30000; coverage += 1000) {
//     results[coverage] = calculatePriceFormula(salary, coverage);
//   }
  
//   return {
//     salary,
//     maxAllowedPayout: adjustedMaxPayout,
//     maxAllowedInsurance,
//     prices: results
//   };
// };

const benefits = [
  "Et outplacement-forløb, der får 8 ud af 10 i job inden for 3 måneder",
  "5 timers online-coaching om året, mens du er i job",
  "Pensionsrådgivning, så du stadig er forsikret, hvis du mister jobbet",
  "Du kan få dækket op til 90% af din løn",
  "Udbetaling, hvis du bliver syg, mens du er ledig",
  "Vi dækker din betaling for den periode, du har fået lønsikringen udbetalt"
];

const DAILY_ALLOWANCE = 22041;
const MIN_SALARY = 29035;

// Coverage amount / period combinations that qualify for a package offer
const PACKAGE_COMBINATIONS: { coverageAmount: number; period: number }[] = [
  { coverageAmount: 3000, period: 6 },
  { coverageAmount: 4000, period: 6 },
  { coverageAmount: 5000, period: 6 },
  { coverageAmount: 6000, period: 6 },
  { coverageAmount: 3000, period: 3 },
  { coverageAmount: 4000, period: 3 },
  { coverageAmount: 5000, period: 3 },
  { coverageAmount: 6000, period: 3 },
];

const isPackageMatch = (coverageAmount: string, period: string) =>
  PACKAGE_COMBINATIONS.some((combo) => combo.coverageAmount === parseInt(coverageAmount) && combo.period === parseInt(period));

interface SalaryInsuranceCalculatorProps {
  isOpen: boolean;
  onBelowThreshold: () => void;
  onClose: () => void;
  onCalculate: (price: number, grossSalary: string, period: string, coverageAmount: string, totalMonthlyPayout: string, leverandoer: string) => void;
  onPackageMatch: () => void;
}

export default function SalaryInsuranceCalculator({ isOpen, onBelowThreshold, onClose, onCalculate, onPackageMatch }: SalaryInsuranceCalculatorProps) {
  const [priceContainer, setPriceContainer] = useState<number>(0);
  const [grossSalary, setGrossSalary] = useState<string>("");
  const [period, setPeriod] = useState<string>("3");
  const [coverageAmount, setCoverageAmount] = useState<string>("2000");
  const [hasManuallyChangedCoverage, setHasManuallyChangedCoverage] = useState<boolean>(false);
  const [isSalaryCardCollapsed, setIsSalaryCardCollapsed] = useState<boolean>(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [underMinimumSalary, setUnderMinimumSalary] = useState<boolean>(false);
  //const [pricingDataArray, setPricingDataArray] = useState<PricingItem>();
  const calculatorRef = useRef<HTMLDivElement>(null);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef() as MutableRefObject<HTMLDivElement>;

  const isEligible = grossSalary !== "" && parseFloat(grossSalary.replace(/\./g, "").replace(",", ".")) >= MIN_SALARY;
  const showError = grossSalary !== "" && !isEligible;
  const showBelowThresholdState = grossSalary !== "" && !isEligible;
  const hasMinimumInput = grossSalary !== "" && parseFloat(grossSalary.replace(/\./g, "").replace(",", ".")) >= 10000;
  
  const [pricingData, setPricingData] = useState<Record<number, Record<number, number>>>();
  
  const form = document.querySelector(`[data-component='salaryinsurance-config-card']`) as HTMLElement;
  const leverandoer = form?.dataset.leverandoer ?? "";


  //Tracking for slider
  const track = () => {
    if(price === 0) return; //Don't track if price is 0, which means data isn't loaded yet
    setTimeout(() => {
       Analytics.getInstance().trackSalaryInsuranceAction("updated", grossSalaryNum.toLocaleString('da-DK'), coverageAmount, period, price.toString(), totalMonthlyPayout.toLocaleString('da-DK'), "salaryinsurance_config")
    }, 2000);
    
  }

  //Tracking for months button
  const setTracking = (newPeriod: string) => {
    setTimeout(() => {
      const priceToTrack = calculatePrice(newPeriod);
      Analytics.getInstance().trackSalaryInsuranceAction("updated", grossSalaryNum.toLocaleString('da-DK'), coverageAmount, newPeriod, priceToTrack.toString(), totalMonthlyPayout.toLocaleString('da-DK'), "salaryinsurance_config")
    }, 2000);
  }

  //Tracking for coverage amount button
  const setCoverageTracking = (newCoverage: string) => {
    setTimeout(() => {
      const priceToTrack = calculatePrice(period, newCoverage);
      const payoutToTrack = calculateTotalMonthlyPayout(newCoverage);
      Analytics.getInstance().trackSalaryInsuranceAction("updated", grossSalaryNum.toLocaleString('da-DK'), newCoverage, period, priceToTrack.toString(), payoutToTrack.toLocaleString('da-DK'), "salaryinsurance_config")
    }, 2000);
  }

  //Tracking cta - selecting salaryinsurance
  const trackSalaryInsuranceSelected = () => {
    setTimeout(() => {
      Analytics.getInstance().trackSalaryInsuranceAction("selected", grossSalaryNum.toLocaleString('da-DK'), coverageAmount, period, price.toString(), totalMonthlyPayout.toLocaleString('da-DK'), "salaryinsurance_config")
    }, 2000);
  }

  //Open and close calculator
  const togglePopup = () => {
    if (isPackageMatch(coverageAmount, period)) {
      onPackageMatch();
    }
    onCalculate(price, grossSalaryNum.toLocaleString('da-DK'), period, parseInt(coverageAmount).toLocaleString('da-DK'), totalMonthlyPayout.toLocaleString('da-DK'), leverandoer);
    onClose();
  }

  
  //Get transaltions file with calculator data from umbraco
  useEffect(() => {
    const fetchData = async () => {
      try { 
        const jsonPricingArray = form?.dataset.jsonPricingArray;
        let pricingDataArray = await fetchSalaryInsuranceData(jsonPricingArray)
        let pricingData: Record<number, Record<number, number>> = mapPricingData(pricingDataArray.data);
        setPricingData(pricingData); 
      } catch (error) {}
    }
 
    fetchData();
    
  }, []);
  

  // Scroll logic for sticky bar
  useEffect(() => {
    const handleScroll = () => {
      if (calculatorRef.current) {
        const rect = calculatorRef.current.getBoundingClientRect();
        setShowStickyBar(rect.bottom < window.innerHeight && window.innerWidth < 768);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d,]/g, "");
    setGrossSalary(value);
    setCoverageAmount("2000");
    setHasManuallyChangedCoverage(false);
    
    // Clear any pending collapse timer on each keystroke
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }

    
    const digitsOnly = value.replace(/[^\d]/g, "");
    
    if (digitsOnly.length >= 5 && value !== "") {
      const salaryNum = parseFloat(value.replace(/\./g, "").replace(",", "."));
      setTimeout(() => {
        Analytics.getInstance().trackSalaryInsuranceAction("updated", salaryNum.toString(), coverageAmount, period, price.toString(), totalMonthlyPayout.toLocaleString('da-DK'), "salaryinsurance_config")
      }, 1200)
      if (salaryNum >= MIN_SALARY) {
        collapseTimerRef.current = setTimeout(() => {
          setIsSalaryCardCollapsed(true);
          if(window.screen.width <= 768 ) {
            scrollRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 1200);
      }
    }
  };

  const calculatePrice = (periodOverride?: string, coverageOverride?: string) => {
    const periodNum = parseInt(periodOverride || period);
    const amountNum = parseInt(coverageOverride || coverageAmount);
    if(pricingData) {
      return pricingData[periodNum]?.[amountNum] || 0;
    } 
    return 0
  };

  const calculateTotalMonthlyPayout = (coverageOverride?: string) => {
    const DAILY_ALLOWANCE = 22041;
    const MAX_TOTAL_PAYOUT = DAILY_ALLOWANCE + 30000;
    const adjustedMaxPayout = Math.round(grossSalaryNum * 0.9 * (1 - 0.08));
    const maxAllowedInsurance = Math.max(0, adjustedMaxPayout - DAILY_ALLOWANCE);
    const selectedInsurance = parseInt(coverageOverride || coverageAmount);
    const actualInsurance = Math.min(selectedInsurance, maxAllowedInsurance, 30000);
    return Math.min(DAILY_ALLOWANCE + actualInsurance, MAX_TOTAL_PAYOUT);
  };

  const price = calculatePrice();  
  const grossSalaryNum = parseFloat(grossSalary.replace(/\./g, "").replace(",", ".")) || 0;
  const maxAllowedPayout = Math.round(grossSalaryNum * 0.9);
  const adjustedMaxPayout = Math.round(grossSalaryNum * 0.9 * (1 - 0.08));
  const maxAllowedInsurance = Math.max(0, adjustedMaxPayout - DAILY_ALLOWANCE);
  const selectedInsurance = parseInt(coverageAmount);
  const actualInsurance = Math.min(selectedInsurance, maxAllowedInsurance, 30000);
  const recommendedCoverage = Math.floor(Math.min(maxAllowedInsurance, 30000) / 1000) * 1000;
  const maxSliderValue = Math.min(30000, recommendedCoverage);
  const isRecommendedCoverage = selectedInsurance === recommendedCoverage;
  const isOverInsured = selectedInsurance > recommendedCoverage;
  const MAX_TOTAL_PAYOUT = DAILY_ALLOWANCE + 30000;
  const totalMonthlyPayout = Math.min(DAILY_ALLOWANCE + actualInsurance, MAX_TOTAL_PAYOUT);
  const coveragePercentage = grossSalaryNum > 0 ? Math.round((totalMonthlyPayout / grossSalaryNum) * 100) : 0;
  const exceedsLimit = selectedInsurance > maxAllowedInsurance;
  const maxSalaryForFullCoverage = Math.ceil(MAX_TOTAL_PAYOUT / 0.9);

  // Visual Breakdown Component
  const VisualBreakdown = () => {
    const displaySalary = Math.min(grossSalaryNum, maxSalaryForFullCoverage);
    const dagpengeWidth = (DAILY_ALLOWANCE / displaySalary) * 100;
    const insuranceWidth = (actualInsurance / displaySalary) * 100;
    
    return (
      <BreakdownWrap>
        <BarLabelsRow>
          <span>0 kr.</span>
          <span>{grossSalaryNum.toLocaleString('da-DK')} kr.</span>
        </BarLabelsRow>
        <BarTrack>
          <BarAllowance 
            initial={{ width: 0 }}
            animate={{ width: `${dagpengeWidth}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
          <BarInsurance 
            initial={{ width: 0 }}
            animate={{ width: `${insuranceWidth}%` }}
            transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
          />
          <BarMarker />
        </BarTrack>
        
        <LegendRow>
          <LegendItem>
            <LegendDot color="#CBD5E1" />
            <LegendText>Dagpenge ({DAILY_ALLOWANCE.toLocaleString('da-DK')} kr.)</LegendText>
          </LegendItem>
          <LegendItem>
            <LegendDot color="#008E9F" />
            <LegendText>Lønsikring ({actualInsurance.toLocaleString('da-DK')} kr.)</LegendText>
          </LegendItem>
        </LegendRow>
      </BreakdownWrap>
    );
  };

  if (!isOpen) return null;

  return (
    <>
    <PopupOverlay>
      <PopupContent>
        <PopupClose onClick={onClose}>
          <X size={20} />
        </PopupClose>
        <Wrapper>
          {/* Confirmation Message */}
          <CalculatorContainer isEligible={isEligible} ref={calculatorRef}>
            
            {/* Gross Salary Card */}
            <SalaryCard collapsed={isEligible && isSalaryCardCollapsed}>
              <AnimatePresence mode="wait">
                {isEligible && isSalaryCardCollapsed ? (
                  <motion.div
                    key="collapsed"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <CollapsedButton onClick={() => setIsSalaryCardCollapsed(false)}>
                      <CollapsedLeft>
                        <CollapsedTextGroup>
                          <CollapsedLabel>Bruttoløn (kr./md.)</CollapsedLabel>
                          <CollapsedValue>
                            {grossSalaryNum.toLocaleString('da-DK')} kr.
                          </CollapsedValue>
                        </CollapsedTextGroup>
                      </CollapsedLeft>
                      <EditBadge>
                        <Pencil size={14} />
                      </EditBadge>
                    </CollapsedButton>
                  </motion.div>
                ) : (
                  <motion.div
                    key="expanded"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <ExpandedContent>
                      <FieldGroup>
                        <FieldLabel htmlFor="grossSalary">
                          Din månedsløn (kr./md.)
                          <Popover>
                            <PopoverTrigger asChild>
                              <InfoTriggerButton className="salaryinsurance-calculator-new-popover2">
                                <Info size={18} />
                              </InfoTriggerButton>
                            </PopoverTrigger>
                            <InfoPopoverContent align="end">
                              <PopoverText>Din månedsløn er din løn før skat og andre fradrag.</PopoverText>
                            </InfoPopoverContent>
                          </Popover>
                        </FieldLabel>
                        <InputWrap>
                          <CurrencyPrefix>kr.</CurrencyPrefix>
                          <SalaryInput
                            id="grossSalary"
                            type="text"
                            inputMode="numeric"
                            value={grossSalary}
                            onChange={handleSalaryChange}
                            placeholder="fx 35.000"
                            hasError={showError}
                            autoFocus
                          />
                        </InputWrap>
                        
                        <AnimatePresence>
                          {showError && (
                            <ErrorBanner 
                              initial={{ opacity: 0, y: -8 }} 
                              animate={{ opacity: 1, y: 0 }}
                            >
                              <AlertCircle size={14} />
                              <span>Minimumsløn er {MIN_SALARY.toLocaleString('da-DK')} kr. for at tegne forsikring.</span>
                            </ErrorBanner>
                          )}
                        </AnimatePresence>
                      </FieldGroup>
                    </ExpandedContent>
                  </motion.div>
                )}
              </AnimatePresence>
            </SalaryCard>

            {/* Main Calculator Interface */}
            <AnimatePresence>
            {/*   {isEligible && hasMinimumInput && ( */}
              {!showBelowThresholdState && /* hasMinimumInput && */ (
                <ResultsSection
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  {/* Controls & Results Container */}
                  <MainGrid>
                    
                    {/* Left Column: Controls */}
                    <ControlsCard>
                      
                      {/* Period Selector */}
                      <PeriodGroup ref={scrollRef}>
                        <BlockLabel>
                          Vælg dækningsperiode
                        </BlockLabel>
                        <MonthButtonRow>
                          {['3', '6', '9'].map((months) => (
                            <MonthButton className="salaryMonthButton" period={period === months ? true : false} key={months} onClick={() => { setPeriod(months); setTracking(months); }}>
                              {months} mdr.
                            </MonthButton>
                          ))}
                        </MonthButtonRow>
                      </PeriodGroup>

                      {/* Coverage Amount Slider */}
                      <SliderGroup>
                        <SliderHeaderRow>
                          <SmallLabel>Vælg månedlig udbetaling</SmallLabel>
                          <SliderValueWrap><SliderValue>{parseInt(coverageAmount).toLocaleString('da-DK')}</SliderValue><SliderUnit>kr./md</SliderUnit></SliderValueWrap>
                        </SliderHeaderRow>

                        <div>
                          <StyledSlider
                            value={[parseInt(coverageAmount)]}
                            min={2000}
                            max={maxSliderValue}
                            step={1000}
                            onValueChange={(vals) => {
                              setCoverageAmount(vals[0].toString());
                              setHasManuallyChangedCoverage(true);
                            }}
                            id="slider"
                            onMouseLeave={() => track()}
                            onTouchEnd={() => track()}
                          />
                          <SliderMetaRow>
                            <span>2.000 kr. (billigst)</span>
                            <span>{Math.min(30000, recommendedCoverage).toLocaleString('da-DK')} kr. (maks)</span>
                          </SliderMetaRow>

                          {/* Recommendation Badge */}
                          <AnimatePresence>
                            {isRecommendedCoverage ? (
                              <RecommendedBadge 
                                animate={{ opacity: 1, height: "auto" }}
                              >
                                <CheckCircle2 size={16} style={{ marginTop: "2px", flexShrink: 0 }} />
                                <div>
                                  <BadgeTitle>Maksimal dækning valgt</BadgeTitle>
                                </div>
                              </RecommendedBadge>
                              ) : (
                              <RecommendCTA
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                onClick={() => {
                                  setCoverageAmount(recommendedCoverage.toString());
                                  setCoverageTracking(recommendedCoverage.toString());
                                }}
                              >
                                Din maksimale dækning <HighlightText>{recommendedCoverage.toLocaleString('da-DK')} kr.</HighlightText>
                                <ChooseLink className="chooseMaxCoverage">Vælg</ChooseLink>
                              </RecommendCTA>
                            )}
                          </AnimatePresence>
                        </div>
                      </SliderGroup>
                    </ControlsCard>

                    {/* Right Column: Visualization & Payout */}
                    <ResultsCard>
                      {/* Teal gradient header */}
                      <ResultsHeader>
                        <ResultsHeaderRow>
                          <ResultsHeaderLabel>Din samlede udbetaling</ResultsHeaderLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <InfoTriggerButtonLight className="salaryinsurance-calculator-new-popover">
                                <Info size={18} />
                              </InfoTriggerButtonLight>
                            </PopoverTrigger>
                            <InfoPopoverContentWide align="center">
                              <PopoverText>
                              Din samlede udbetaling er dagpenge og lønsikring før skat.
                              </PopoverText>
                            </InfoPopoverContentWide>
                          </Popover>
                        </ResultsHeaderRow>
                        
                        <ResultsAmountWrap 
                          key={totalMonthlyPayout}
                          initial={{ scale: 1.02, opacity: 0.8 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.4, ease: "easeInOut" }}
                        >
                          <ResultsAmount>
                            {totalMonthlyPayout.toLocaleString('da-DK')}
                          </ResultsAmount>
                          <ResultsAmountUnit>kr. / måned</ResultsAmountUnit>
                        </ResultsAmountWrap>
                      </ResultsHeader>
                      
                      {/* Chart area */}
                      <ChartArea>
                        <VisualBreakdown />
                      </ChartArea>
                    </ResultsCard>
                  </MainGrid>

                  {/* CTA + Benefits Section (combined) */}
                  <CtaCard>
                    {/* CTA area */}
                    <CtaTop>
                      <CtaHeadingRow>
                        <CtaSubtitle>Din pris for lønsikring</CtaSubtitle>
                      </CtaHeadingRow>
                      <CtaRow>
                        <PriceWrap>
                          <PriceValue>
                            {price.toLocaleString('da-DK')}
                            <PriceUnit>kr./md.</PriceUnit>
                          </PriceValue>
                        </PriceWrap>
                        <NextButton className="button" onClick={() => {togglePopup(), trackSalaryInsuranceSelected()}}>
                          Vælg denne dækning
                        </NextButton>
                      </CtaRow>
                      <Disclaimer>
                        *Forudsætter medlemskab af Ase A-kasse. Priser er vejledende.
                      </Disclaimer>
                    </CtaTop>
                  </CtaCard>
                </ResultsSection>
                )}
              {/* )} */}
            </AnimatePresence>

            {/* Below Threshold State */}
            <AnimatePresence>
              {showBelowThresholdState && hasMinimumInput && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                >
                  <BelowThresholdCard>
                    <BelowThresholdInner>
                      <BelowHeading>
                        Du er godt dækket med dagpenge - hvis du har a-kasse
                      </BelowHeading>
                      <BelowSubtitle>
                        Lønsikring giver ikke mening for dig. Men dagpenge er din økonomiske sikkerhed, hvis du mister jobbet.
                      </BelowSubtitle>
                      <BelowBenefitsWrap>    
                        <div>
                          <BelowBenefitsTitle>Plus du får:</BelowBenefitsTitle>
                          <BelowBenefitsList>
                            {[
                              "Dagpenge op til 26.198 kr./md.",
                              "Hjælp til at komme hurtigt videre i job",
                              "Onlinekurser og efteruddannelse",
                              "Adgang til attraktive medlemsrabatter"
                            ].map((item, i) => (
                              <BelowBenefitItem key={i}>
                                <BelowBullet>•</BelowBullet>
                                <BelowBenefitText>{item}</BelowBenefitText>
                              </BelowBenefitItem>
                            ))}
                          </BelowBenefitsList>
                        </div>
                      </BelowBenefitsWrap>
                      
                      <a 
                        onClick={() => onBelowThreshold()}
                        className="button"
                      >Vælg a-kasse</a>
                    </BelowThresholdInner>
                  </BelowThresholdCard>
                </motion.div>
              )}
            </AnimatePresence>
          </CalculatorContainer>
        </Wrapper>
      </PopupContent>
    </PopupOverlay>
    </>
  );
}